import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import { createTextFilter } from '../../filters/types/textFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/TableLink';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export type TableColumnFormatText<TRow> = {
  type: 'text';
  field: KeyOfType<TRow, string | string[] | null>;
  multiline?: boolean;
} & TableColumnFormatLink<TRow>;

type TextColumnConfig<TRow, TDataType> = TableDataColumnConfig<
  TRow,
  TDataType
> &
  TableLinkColumnConfig<TRow> & { multiline?: boolean };

export const createTextColumn: {
  <TRow>(
    config: TextColumnConfig<TRow, string | null> & { array?: false },
  ): TableColumnBase<TRow, TableColumnFormatText<TRow>>;
  <TRow>(
    config: TextColumnConfig<TRow, string[] | null> & { array: true },
  ): TableColumnBase<TRow, TableColumnFormatText<TRow>>;
} = <TRow>(
  config: TextColumnConfig<TRow, string | string[] | null>,
): TableColumnBase<TRow, TableColumnFormatText<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'text',
    field: config.key,
    multiline: config.multiline,
    createLinkProps: config.link?.createLinkProps,
  },
  csvStringDependencies: [config.key],
  webDependencies: mergeFieldDependencies(
    config.key,
    config.link?.dependencies,
  ),
  toCsvString: (row) =>
    formatValueToCsvString(
      getValueFromKeyOfType<TRow, string | string[] | null>(row, config.key),
      (v: string | null): string => v ?? '',
    ),
  filter: createDefaultFilter(config.filter, createTextFilter()),
});
